import React from "react";
import {
  PhoneCall,
  ClipboardList,
  MapPinned,
  CalendarCheck2,
  HeartHandshakeIcon,
} from "lucide-react";
import { motion } from "framer-motion";

const Platform = () => {
  const steps = [
    {
      id: "01",
      icon: <PhoneCall size={28} className="text-royalBlue" strokeWidth={1.5} />,
      title: "Reach Out to Livmo",
      description:
        "Our subscribers contact Livmo whenever healthcare support or medical guidance is needed.",
    },
    {
      id: "02",
      icon: <ClipboardList size={28} className="text-royalGreen" strokeWidth={1.5} />,
      title: "Requirement Understanding",
      description:
        "Our healthcare coordination team understands your medical requirement and handle situation carefully.",
    },
    {
      id: "03",
      icon: <MapPinned size={28} className="text-royalBlue" strokeWidth={1.5} />,
      title: "Provider Matching",
      description:
        "We connect you with the best nearby verified hospitals, clinics, labs, or specialists.",
    },
    {
      id: "04",
      icon: <CalendarCheck2 size={28} className="text-royalGreen" strokeWidth={1.5} />,
      title: "Complete Coordination",
      description:
        "Appointments, healthcare coordination, support, and guidance are provided throughout the process.",
    },
  ];

  return (
    <motion.section
      id="HowItWorks"
      initial={{ opacity: 0, x: -40 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.8,
        ease: "easeIn"
      }}
      viewport={{ once: true, amount: 0.2 }}
      className="bg-[#F8FAFF] py-[6vw] lg:py-[3vw] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-[5%]">
        
        {/* Heading */}
        <div className="text-center">
        <div className="flex justify-center mb-8 md:mb-10">
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-5 py-2 sm:px-6 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
    <HeartHandshakeIcon size={18} className="text-royalGreen" />
    <p className="font-poppins font-semibold text-royalBlue text-sm sm:text-base md:text-lg tracking-wide uppercase whitespace-nowrap">
      HOW LIVMO HELPS SUBSCRIBERS
    </p>
  </div>
</div>

          <h2 className="mt-4 font-poppins font-[700] text-darkblue leading-[1.2] text-[8vw] sm:text-[6vw] md:text-[4vw] lg:text-[3vw]">
            How Livmo Supports Subscribers
          </h2>

          <p className="mt-6 max-w-[850px] mx-auto text-text1 leading-[1.9] font-poppins text-[3.8vw] sm:text-[2.5vw] md:text-[1.5vw] lg:text-[1.05vw]">
            Livmo simplifies healthcare coordination for our subscribers
            through a guided support system that connects you with trusted
            healthcare providers quickly and efficiently.
          </p>
        </div>

        {/* Steps */}
        <div className="relative mt-[12vw] lg:mt-[6vw]">
          
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-[70px] left-[10%] w-[80%] h-[2px] bg-gradient-to-r from-royalBlue via-[#B8C8FF] to-royalGreen"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-[2vw] p-8 shadow-xl border border-[#EDF2FF] hover:-translate-y-3 transition-all duration-300 relative overflow-hidden"
              >
                
                {/* Top Gradient */}
                <div className="absolute top-0 left-0 w-full h-[5px] bg-gradient-to-r from-royalBlue to-royalGreen"></div>

                {/* Icon and Number Row */}
                <div className="flex items-center justify-between">
                  <div className="w-[60px] h-[60px] rounded-xl bg-gradient-to-br from-royalBlue/10 to-royalGreen/10 flex items-center justify-center">
                    {step.icon}
                  </div>

                  <span className="font-poppins font-[800] text-[#E8EEFF] text-[5vw] lg:text-[2.5vw] tracking-tighter">
                    {step.id}
                  </span>
                </div>

                {/* Content */}
                <div className="mt-6">
                  <h4 className="font-poppins font-[700] text-darkblue leading-[1.4] text-[5vw] sm:text-[2.8vw] md:text-[2vw] lg:text-[1.25vw]">
                    {step.title}
                  </h4>

                  <p className="mt-3 text-text1 leading-[1.8] font-poppins text-[3.5vw] sm:text-[2vw] md:text-[1.3vw] lg:text-[0.9vw]">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Highlight */}
        <div className="mt-[12vw] lg:mt-[6vw] bg-gradient-to-r from-royalBlue to-royalGreen rounded-[2vw] px-[8%] py-[8%] lg:py-[4%] text-center">
          <h3 className="text-white font-poppins font-[700] leading-[1.4] text-[6vw] sm:text-[4vw] md:text-[2.5vw] lg:text-[2vw]">
            Healthcare Support Made Simple for Our Subscribers
          </h3>

          <p className="text-white/90 mt-4 max-w-[900px] mx-auto leading-[1.9] font-poppins text-[3.5vw] sm:text-[2vw] md:text-[1.4vw] lg:text-[1vw]">
            Livmo acts as your healthcare coordination partner — helping you
            access trusted healthcare services without confusion, delays, or
            unnecessary stress.
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default Platform;